import { useState } from 'react';
import { Group } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { CustomerFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { useUninvoicedDispatchesReport } from '../api';
import { formatDate, formatMoneyCents, formatQty } from '../format';
import type { UninvoicedDispatchRow } from '../types';

/** Report #8 (BUILD_SPEC.md §13) — posted dispatch lines with no invoice line yet
 * (an anti-join against invoice_lines.dispatch_line_id). */
export function UninvoicedDispatchesReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);

  const query = useUninvoicedDispatchesReport({ siteId: siteId ?? undefined, customerId: customerId ?? undefined });
  const rows = query.data?.items ?? [];

  const columns: ReportColumn<UninvoicedDispatchRow>[] = [
    { key: 'dispatch', header: 'Dispatch', render: (r) => r.dispatchNumber },
    { key: 'dispatchedAt', header: 'Dispatched', render: (r) => formatDate(r.dispatchedAt) },
    { key: 'customer', header: 'Customer', render: (r) => r.customerName },
    { key: 'product', header: 'Product', render: (r) => `${r.productSku} — ${r.productName}` },
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => formatQty(r.qty) },
    { key: 'sale', header: 'Sale value', align: 'right', render: (r) => formatMoneyCents(r.saleValueCents) },
  ];

  return (
    <ReportPage
      title="Uninvoiced dispatches"
      description="Posted dispatches with at least one line not yet on an invoice."
      action={
        <CsvDownloadButton
          path="/reporting/uninvoiced-dispatches"
          query={{ siteId: siteId ?? undefined, customerId: customerId ?? undefined }}
          filename="uninvoiced-dispatches.csv"
          description="Exports the uninvoiced dispatch lines, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={220} />
        <CustomerFilterSelect value={customerId} onChange={setCustomerId} label="Customer" w={240} />
      </Group>

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.lineId}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load uninvoiced dispatches."
        emptyMessage="Nothing uninvoiced right now"
        emptyHint="Every posted dispatch has been invoiced."
        emptyIcon={IconCircleCheck}
      />
    </ReportPage>
  );
}
